import { supabase } from "./supabase";
import type {
  Template,
  Simulation,
  Report,
  UserProfile,
  Certificate,
} from "./types";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

/**
 * Erro estruturado da API — preserva status + code pro frontend distinguir
 * guardrail (422 BLOCKED_BY_AGE_GUARDRAIL) de erros genéricos.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options?.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, err.code, err.message || "API error");
  }
  return res.json();
}

export const api = {
  syncUser: () =>
    request<{ id: string; email: string }>("/auth/sync", { method: "POST" }),

  getMe: () => request<UserProfile>(`/user/me`),

  getTemplates: (locale = "en-US") =>
    request<Template[]>(`/templates?locale=${locale}`),

  createSimulation: (data: {
    userId: string;
    voiceCloneId: string;
    templateId: string;
    familyPhone: string;
    familyName?: string;
    consentGiven: boolean;
    scriptUsed?: string;
    scriptTone?: string;
  }) =>
    request<Simulation>("/simulation/create", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getSimulation: (id: string) => request<Simulation>(`/simulation/${id}`),

  getSimulations: () => request<Simulation[]>(`/simulation/user`),

  getReport: (simulationId: string) =>
    request<Report>(`/simulation/${simulationId}/report`),

  createCheckoutSession: (simulationId: string | null, type: "single" | "annual" = "single") =>
    request<{ id: string; url: string }>("/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ simulationId, type }),
    }),

  useCredit: (simulationId: string) =>
    request<{ success: boolean }>("/billing/use-credit", {
      method: "POST",
      body: JSON.stringify({ simulationId }),
    }),

  submitLead: (data: {
    name: string;
    organization: string;
    email: string;
    useCase: string;
    estimatedUsers: string;
  }) =>
    request<{ success: boolean; leadId: string }>("/leads/apply", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateConsent: (simulationId: string) =>
    request<{ status: string }>(`/simulation/${simulationId}/consent`, {
      method: "PATCH",
    }),

  startOutbound: (simulationId: string) =>
    request<{ message: string }>("/simulation/outbound", {
      method: "POST",
      body: JSON.stringify({ simulationId }),
    }),

  async transcribeChunk(audioBlob: Blob): Promise<{ transcript: string }> {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append("audio", audioBlob, "chunk.webm");

    const res = await fetch(`${API_URL}/voice/transcribe`, {
      method: "POST",
      headers: { ...authHeaders },
      body: formData,
    });
    if (!res.ok) throw new Error("Failed to transcribe chunk");
    return res.json();
  },

  cloneVoice: async (
    userId: string,
    audioBlob: Blob,
    proofOfLifeKey: string,
    tone?: string,
    customScript?: string,
    audioDurationSeconds?: number,
  ): Promise<{ jobId: string }> => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append("audio", audioBlob, "voice.webm");
    formData.append("userId", userId);
    formData.append("proofOfLifeKey", proofOfLifeKey);
    if (tone) formData.append("tone", tone);
    if (customScript) formData.append("customScript", customScript);
    if (audioDurationSeconds !== undefined) {
      formData.append("audioDurationSeconds", String(audioDurationSeconds));
    }
    const res = await fetch(`${API_URL}/voice/clone`, {
      method: "POST",
      headers: authHeaders,
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message || `Voice clone failed (${res.status})`);
    }
    return res.json();
  },

  getCloneAudio: (voiceCloneId: string) =>
    request<{ audioUrl: string }>(`/voice/clone/${voiceCloneId}/audio`),

  pollCloneJob: async (
    jobId: string,
  ): Promise<{
    status: string;
    result?: { voiceCloneId: string; audioUrl: string };
    error?: string;
  }> => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${API_URL}/voice/clone/job/${jobId}`, {
      headers: authHeaders,
    });
    if (!res.ok) throw new Error("Failed to check clone status");
    return res.json();
  },

  transcribeAudio: async (audioBlob: Blob): Promise<{ text: string }> => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append("audio", audioBlob, "context.webm");
    const res = await fetch(`${API_URL}/voice/transcribe`, {
      method: "POST",
      headers: authHeaders,
      body: formData,
    });
    if (!res.ok) throw new Error("Transcription failed");
    return res.json();
  },

  generateScript: async (data: {
    userContext: string;
    baseTemplate?: string;
    tone: string;
    familyAge?: string;
  }): Promise<{ audioScript: string; emotionalContext: string }> => {
    return request<{ audioScript: string; emotionalContext: string }>(
      "/voice/generate-script",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  },

  downloadReportPdf: async (simulationId: string): Promise<Blob> => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${API_URL}/simulation/${simulationId}/report/pdf`,
      { headers: authHeaders },
    );
    if (!res.ok) throw new Error("Failed to download PDF");
    return res.blob();
  },

  downloadCertificatePdf: async (simulationId: string): Promise<Blob> => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${API_URL}/simulation/${simulationId}/certificate/pdf`,
      { headers: authHeaders },
    );
    if (!res.ok) throw new Error("Failed to download certificate PDF");
    return res.blob();
  },

  createShareLink: (simulationId: string) =>
    request<{ shareUrl: string; expiresAt: string }>(
      `/simulation/${simulationId}/report/share`,
      { method: "POST" },
    ),

  getPublicReport: async (token: string) => {
    const res = await fetch(`${API_URL}/simulation/report/public/${token}`);
    if (!res.ok) throw new Error("Report not found or expired");
    return res.json();
  },

  getCertificate: (simulationId: string) =>
    request<{ hasCertificate: boolean; certificate?: Certificate }>(
      `/simulation/${simulationId}/certificate`,
    ),

  getCertificateVerification: async (shareToken: string) => {
    const res = await fetch(
      `${API_URL}/simulation/certificate/verify/${shareToken}`,
    );
    if (!res.ok) throw new Error("Certificate not found or invalid");
    return res.json();
  },

  requestRefund: (simulationId: string) =>
    request<{ refunded: boolean }>(`/billing/refund/${simulationId}`, {
      method: "POST",
    }),

  deleteAccount: () =>
    request<{ deleted: boolean }>("/user/me", { method: "DELETE" }),

  updateProfile: (data: {
    name?: string;
    familyRole?: string;
    customName?: string;
    onboardingCompleted?: boolean;
  }) =>
    request<UserProfile>("/user/me", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
};
